/**
 * Admin Reseller Detail Page
 * Master Admin management view for a specific reseller
 * - View reseller profile
 * - Create dealers under this reseller
 * - Assign existing dealers
 * - View/manage assigned dealers with economics
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import ResellerTeamPanel from "../../components/ResellerTeamPanel";
import ConquestAccessPanel from "../../components/ConquestAccessPanel";

const formatDate = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === 0) return "—";
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const AdminResellerDetailPage = ({ resellerId, onNavigate }) => {
  const { apiFetch, user } = useAuth();
  const { success: showToast, error: showError } = useToast();
  
  const [reseller, setReseller] = useState(null);
  const [loading, setLoading] = useState(true);

  // Conquest Audit Credits
  const [conquest, setConquest] = useState({ balance: 0, dealers: [] });
  const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
  const [creditsForm, setCreditsForm] = useState({ delta: "", note: "", dealer_id: "" });
  const [savingCredits, setSavingCredits] = useState(false);
  const [showDealerLinkModal, setShowDealerLinkModal] = useState(false);
  const [editingDealerLink, setEditingDealerLink] = useState(null);
  const [dealerLinkForm, setDealerLinkForm] = useState({ name: "", view_report_url: "", demo_url: "" });
  const [savingDealerLink, setSavingDealerLink] = useState(false);
  const [deductingId, setDeductingId] = useState(null);
  
  // Create Dealer Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    dealer_name: "",
    dealer_admin_email: "",
    invite_mode: "none",
    temp_password: "",
    confirm_password: "",
    reseller_cost: "",
    reseller_retail: "",
  });
  
  // Assign Dealer Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [availableDealers, setAvailableDealers] = useState([]);
  const [loadingDealers, setLoadingDealers] = useState(false);
  const [assignForm, setAssignForm] = useState({
    dealer_business_id: "",
    reseller_cost: "",
    reseller_retail: "",
  });
  const [dealerSearch, setDealerSearch] = useState("");

  const fetchReseller = useCallback(async () => {
    if (!resellerId) return;
    try {
      setLoading(true);
      const response = await apiFetch(`/admin/resellers/${resellerId}`);
      setReseller(response.data);
    } catch (err) {
      showError("Failed to load reseller details");
      console.error("Failed to load reseller:", err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, resellerId, showError]);

  const fetchAvailableDealers = useCallback(async (search = "") => {
    try {
      setLoadingDealers(true);
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await apiFetch(`/admin/resellers/${resellerId}/available-dealers${params}`);
      setAvailableDealers(response.data.dealers || []);
    } catch (err) {
      console.error("Failed to load available dealers:", err);
    } finally {
      setLoadingDealers(false);
    }
  }, [apiFetch, resellerId]);

  const fetchConquest = useCallback(async () => {
    if (!resellerId) return;
    try {
      const [creditsRes, dealersRes] = await Promise.all([
        apiFetch(`/admin/resellers/${resellerId}/conquest/credits`),
        apiFetch(`/admin/resellers/${resellerId}/conquest/dealers`),
      ]);
      setConquest({
        balance: creditsRes.data.balance || 0,
        history: creditsRes.data.history || [],
        dealers: dealersRes.data.dealers || [],
      });
    } catch (err) {
      console.error("Failed to load Conquest Audit Credits data:", err);
    }
  }, [apiFetch, resellerId]);

  useEffect(() => {
    fetchReseller();
    fetchConquest();
  }, [fetchReseller, fetchConquest]);

  useEffect(() => {
    if (showAssignModal) {
      fetchAvailableDealers();
    }
  }, [showAssignModal, fetchAvailableDealers]);

  const handleCreateDealer = async () => {
    if (!createForm.dealer_name.trim()) {
      showError("Dealer name is required");
      return;
    }
    
    if (createForm.invite_mode === "temp_password") {
      if (!createForm.temp_password || createForm.temp_password.length < 8) {
        showError("Password must be at least 8 characters");
        return;
      }
      if (createForm.temp_password !== createForm.confirm_password) {
        showError("Passwords do not match");
        return;
      }
      if (!createForm.dealer_admin_email) {
        showError("Admin email is required when setting a password");
        return;
      }
    }
    
    if (createForm.invite_mode === "invite" && !createForm.dealer_admin_email) {
      showError("Admin email is required when sending an invite");
      return;
    }

    try {
      setCreating(true);
      const payload = {
        dealer_name: createForm.dealer_name.trim(),
        invite_mode: createForm.invite_mode,
      };
      
      if (createForm.dealer_admin_email) {
        payload.dealer_admin_email = createForm.dealer_admin_email.trim().toLowerCase();
      }
      if (createForm.invite_mode === "temp_password") {
        payload.temp_password = createForm.temp_password;
      }
      if (createForm.reseller_cost) {
        payload.reseller_cost = parseFloat(createForm.reseller_cost);
      }
      if (createForm.reseller_retail) {
        payload.reseller_retail = parseFloat(createForm.reseller_retail);
      }

      await apiFetch(`/admin/resellers/${resellerId}/dealers`, {
        method: "POST",
        data: payload,
      });

      showToast("Dealer created and assigned to reseller");
      setShowCreateModal(false);
      resetCreateForm();
      fetchReseller();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to create dealer");
    } finally {
      setCreating(false);
    }
  };

  const handleAssignDealer = async () => {
    if (!assignForm.dealer_business_id) {
      showError("Please select a dealer");
      return;
    }

    try {
      setAssigning(true);
      const payload = {
        dealer_business_id: assignForm.dealer_business_id,
      };
      if (assignForm.reseller_cost) {
        payload.reseller_cost = parseFloat(assignForm.reseller_cost);
      }
      if (assignForm.reseller_retail) {
        payload.reseller_retail = parseFloat(assignForm.reseller_retail);
      }

      await apiFetch(`/admin/resellers/${resellerId}/assign-dealer`, {
        method: "POST",
        data: payload,
      });

      showToast("Dealer assigned to reseller");
      setShowAssignModal(false);
      resetAssignForm();
      fetchReseller();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to assign dealer");
    } finally {
      setAssigning(false);
    }
  };

  const handleAddCredits = async () => {
    const delta = parseInt(creditsForm.delta, 10);
    if (!delta || delta === 0) {
      showError("Enter a non-zero number of credits");
      return;
    }
    if (delta < 0 && !creditsForm.dealer_id) {
      showError("Select which dealer this deduction is for");
      return;
    }
    try {
      setSavingCredits(true);
      await apiFetch(`/admin/resellers/${resellerId}/conquest/credits`, {
        method: "POST",
        data: {
          delta, note: creditsForm.note.trim() || undefined,
          dealer_id: delta < 0 ? creditsForm.dealer_id : undefined,
        },
      });
      showToast(`${delta > 0 ? "Added" : "Removed"} ${Math.abs(delta)} audit credit${Math.abs(delta) === 1 ? "" : "s"}`);
      setShowAddCreditsModal(false);
      setCreditsForm({ delta: "", note: "", dealer_id: "" });
      fetchConquest();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to update credits");
    } finally {
      setSavingCredits(false);
    }
  };

  const openDealerLinkModal = (link = null) => {
    setEditingDealerLink(link);
    setDealerLinkForm(link
      ? { name: link.name, view_report_url: link.view_report_url || "", demo_url: link.demo_url || "" }
      : { name: "", view_report_url: "", demo_url: "" });
    setShowDealerLinkModal(true);
  };

  const handleSaveDealerLink = async () => {
    if (!dealerLinkForm.name.trim()) {
      showError("Dealer name is required");
      return;
    }
    try {
      setSavingDealerLink(true);
      const payload = {
        name: dealerLinkForm.name.trim(),
        view_report_url: dealerLinkForm.view_report_url.trim() || null,
        demo_url: dealerLinkForm.demo_url.trim() || null,
      };
      if (editingDealerLink) {
        await apiFetch(`/admin/resellers/${resellerId}/conquest/dealers/${editingDealerLink.id}`, {
          method: "PUT", data: payload,
        });
        showToast("Dealer link updated");
      } else {
        await apiFetch(`/admin/resellers/${resellerId}/conquest/dealers`, {
          method: "POST", data: payload,
        });
        showToast("Dealer link added");
      }
      setShowDealerLinkModal(false);
      fetchConquest();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to save dealer link");
    } finally {
      setSavingDealerLink(false);
    }
  };

  const handleDeleteDealerLink = async (link) => {
    const refundNote = link.status === "requested"
      ? " This does NOT auto-refund the credit that was deducted when it was requested — use \"Add Credits\" with a positive number if you want to refund it."
      : "";
    if (!window.confirm(`Remove "${link.name}" from Conquest Audits for this reseller?${refundNote}`)) return;
    try {
      await apiFetch(`/admin/resellers/${resellerId}/conquest/dealers/${link.id}`, { method: "DELETE" });
      showToast("Dealer link removed");
      fetchConquest();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to remove dealer link");
    }
  };

  const handleQuickDeductCredit = async (link) => {
    if (!window.confirm(`Remove 1 audit credit for "${link.name}"?`)) return;
    try {
      setDeductingId(link.id);
      await apiFetch(`/admin/resellers/${resellerId}/conquest/credits`, {
        method: "POST",
        data: { delta: -1, dealer_id: link.id, note: `Quick deduction — ${link.name}` },
      });
      showToast(`Removed 1 audit credit for ${link.name}`);
      fetchConquest();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to remove credit");
    } finally {
      setDeductingId(null);
    }
  };

  const handleUnassignDealer = async (dealerId, dealerName) => {
    if (!window.confirm(`Remove "${dealerName}" from this reseller? The dealer will still exist but won't be visible to this reseller.`)) {
      return;
    }

    try {
      await apiFetch(`/admin/resellers/${resellerId}/dealers/${dealerId}`, {
        method: "DELETE",
      });
      showToast(`Dealer "${dealerName}" unassigned from reseller`);
      fetchReseller();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to unassign dealer");
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      dealer_name: "",
      dealer_admin_email: "",
      invite_mode: "none",
      temp_password: "",
      confirm_password: "",
      reseller_cost: "",
      reseller_retail: "",
    });
  };

  const resetAssignForm = () => {
    setAssignForm({
      dealer_business_id: "",
      reseller_cost: "",
      reseller_retail: "",
    });
    setDealerSearch("");
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard`);
  };

  // RBAC check
  if (user?.role !== "MASTER_ADMIN") {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don&apos;t have permission to access this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 w-full">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-32 w-full mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!reseller) {
    return (
      <div className="p-6 w-full">
        <Card>
          <CardContent className="py-16 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Reseller Not Found</h2>
            <Button onClick={() => onNavigate("admin-resellers")}>Back to Resellers</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 w-full" data-testid="admin-reseller-detail-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => onNavigate("admin-resellers")} 
        className="mb-4 text-gray-600"
        data-testid="back-to-resellers"
      >
        ← Back to Resellers
      </Button>

      {/* Header Section */}
      <Card className="mb-6">
        <CardContent className="py-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900" data-testid="reseller-name">
                  {reseller.name}
                </h1>
                <Badge className={reseller.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                  {reseller.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                <div>
                  <p className="text-gray-500">Admin Email</p>
                  <p className="font-medium">{reseller.admin_user?.email || "No admin"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Contact Name</p>
                  <p className="font-medium">{reseller.contact_name || "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{reseller.contact_phone || "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Dealers</p>
                  <p className="font-medium">{reseller.summary?.total_dealers || 0}</p>
                </div>
              </div>
              
              {/* Economics Summary */}
              {reseller.summary?.total_dealers > 0 && (
                <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm font-medium text-indigo-900 mb-2">Monthly Economics</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-indigo-600">Total Cost</p>
                      <p className="font-semibold text-indigo-900">{formatCurrency(reseller.summary?.total_cost)}</p>
                    </div>
                    <div>
                      <p className="text-indigo-600">Total Retail</p>
                      <p className="font-semibold text-indigo-900">{formatCurrency(reseller.summary?.total_retail)}</p>
                    </div>
                    <div>
                      <p className="text-indigo-600">Est. Monthly Profit</p>
                      <p className="font-semibold text-green-700">{formatCurrency(reseller.summary?.estimated_monthly_profit)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2 ml-4">
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-dealer-btn"
              >
                + Create Dealer
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowAssignModal(true)}
                data-testid="assign-dealer-btn"
              >
                Assign Existing
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dealers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Dealers Under This Reseller</CardTitle>
          <CardDescription>
            {reseller.assigned_dealers?.length || 0} dealers assigned
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reseller.assigned_dealers?.length === 0 ? (
            <div className="text-center py-12" data-testid="no-dealers-message">
              <div className="text-4xl mb-4">🏪</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No dealers assigned yet</h3>
              <p className="text-gray-500 mb-4">Create or assign dealers to this reseller</p>
              <Button onClick={() => setShowCreateModal(true)} data-testid="create-first-dealer-btn">
                Create Dealer
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="dealers-table">
                <thead>
                  <tr className="border-b text-left text-sm text-gray-500">
                    <th className="pb-3 font-medium">Dealer Name</th>
                    <th className="pb-3 font-medium">Public Key</th>
                    <th className="pb-3 font-medium text-center">Leads</th>
                    <th className="pb-3 font-medium text-center">Status</th>
                    <th className="pb-3 font-medium text-right">Retail</th>
                    <th className="pb-3 font-medium text-right">Cost</th>
                    <th className="pb-3 font-medium text-right">Margin</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {reseller.assigned_dealers.map((dealer) => (
                    <tr 
                      key={dealer.dealer_id} 
                      className="hover:bg-gray-50"
                      data-testid={`dealer-row-${dealer.dealer_id}`}
                    >
                      <td className="py-4">
                        <p className="font-medium text-gray-900">{dealer.dealer_name}</p>
                        <p className="text-xs text-gray-500">Assigned {formatDate(dealer.assigned_at)}</p>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                            {dealer.public_key?.substring(0, 12)}...
                          </code>
                          <button
                            onClick={() => copyToClipboard(dealer.public_key, "Public key")}
                            className="text-gray-400 hover:text-gray-600 text-sm"
                            title="Copy public key"
                          >
                            📋
                          </button>
                        </div>
                      </td>
                      <td className="py-4 text-center">
                        <span className="text-sm font-medium">{dealer.lead_count || 0}</span>
                      </td>
                      <td className="py-4 text-center">
                        <Badge className={dealer.dealer_is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                          {dealer.dealer_is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="py-4 text-right text-sm">
                        {formatCurrency(dealer.reseller_retail)}
                      </td>
                      <td className="py-4 text-right text-sm">
                        {formatCurrency(dealer.reseller_cost)}
                      </td>
                      <td className="py-4 text-right text-sm font-medium text-green-700">
                        {dealer.monthly_margin > 0 ? formatCurrency(dealer.monthly_margin) : "—"}
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onNavigate("admin-dealer-detail", dealer.dealer_id)}
                            data-testid={`view-dealer-${dealer.dealer_id}`}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleUnassignDealer(dealer.dealer_id, dealer.dealer_name)}
                            data-testid={`remove-dealer-${dealer.dealer_id}`}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team */}
      <div className="mt-6">
        <ResellerTeamPanel businessId={resellerId} canManage={true} />
      </div>

      {/* Conquest Audit Access (Stage 4-complete Conquest Roles module) */}
      <div className="mt-6">
        <ConquestAccessPanel resellerId={resellerId} resellerName={reseller.name} />
      </div>

      {/* Conquest Audit Credits */}
      <Card className="mt-6" data-testid="conquest-audit-credits-admin-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Conquest Audit Credits</CardTitle>
              <CardDescription>Credit balance and per-dealer report/demo links shown on the reseller&apos;s side.</CardDescription>
            </div>
            <Button onClick={() => setShowAddCreditsModal(true)} data-testid="add-credits-btn">
              + Add Credits
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg flex items-center justify-between">
            <span className="text-sm font-medium text-indigo-900">Current Balance</span>
            <span className="text-2xl font-bold text-indigo-900" data-testid="conquest-credit-balance">
              {conquest.balance} credit{conquest.balance === 1 ? "" : "s"}
            </span>
          </div>

          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">Dealers (Reports &amp; Demo Links)</h3>
            <Button variant="outline" size="sm" onClick={() => openDealerLinkModal(null)} data-testid="add-conquest-dealer-btn">
              + Add Dealer
            </Button>
          </div>
          {conquest.dealers.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center" data-testid="no-conquest-dealers-message">
              No dealers added yet for Conquest Audits.
            </p>
          ) : (
            <div className="divide-y" data-testid="conquest-dealers-list">
              {conquest.dealers.map((d) => (
                <div key={d.id} className="py-3 flex items-center justify-between" data-testid={`conquest-dealer-row-${d.id}`}>
                  <div>
                    <p className="font-medium text-gray-900 flex items-center gap-2">
                      {d.name}
                    </p>
                    {d.status === "requested" && (
                      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 mt-1"
                             data-testid={`conquest-dealer-pending-badge-${d.id}`}>
                        Pending request
                      </Badge>
                    )}
                    {d.requested_by_email && (
                      <p className="text-xs text-gray-500 mt-0.5" data-testid={`conquest-dealer-requested-by-${d.id}`}>
                        Requested by: {d.requested_by_email}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {d.view_report_url ? "Report ✓" : "Report —"} &nbsp;·&nbsp; {d.demo_url ? "Demo ✓" : "Demo —"}
                      {d.website_url && <> &nbsp;·&nbsp; Site: {d.website_url}</>}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openDealerLinkModal(d)} data-testid={`edit-conquest-dealer-${d.id}`}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-orange-600 hover:bg-orange-50"
                            onClick={() => handleQuickDeductCredit(d)} disabled={deductingId === d.id}
                            data-testid={`deduct-credit-conquest-dealer-${d.id}`}>
                      {deductingId === d.id ? "Removing…" : "-1 Credit"}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteDealerLink(d)} data-testid={`delete-conquest-dealer-${d.id}`}>
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {conquest.history?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Recent Credit Activity</h3>
              <div className="divide-y border rounded-lg" data-testid="conquest-credit-history">
                {conquest.history.map((h, i) => (
                  <div key={i} className="px-3 py-2 flex items-center justify-between text-sm gap-3">
                    <span className={h.delta > 0 ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
                      {h.delta > 0 ? "+" : ""}{h.delta} credit{Math.abs(h.delta) === 1 ? "" : "s"}
                    </span>
                    <span className="text-gray-600 truncate">{h.dealer_name || "—"}</span>
                    <span className="text-gray-500 text-xs truncate" data-testid={`conquest-history-actor-${i}`}>
                      {h.created_by_email || ""}
                    </span>
                    <span className="text-gray-400 text-xs flex-shrink-0">
                      {h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Credits Modal */}
      <Dialog open={showAddCreditsModal} onOpenChange={(open) => { if (!open) setShowAddCreditsModal(false); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add Conquest Audit Credits</DialogTitle>
            <DialogDescription>Applies immediately to {reseller.name}&apos;s balance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="credits_delta">Credits (use a negative number to remove) *</Label>
              <Input id="credits_delta" type="number" placeholder="e.g. 10"
                     value={creditsForm.delta}
                     onChange={(e) => setCreditsForm(f => ({ ...f, delta: e.target.value }))}
                     data-testid="credits-delta-input" />
            </div>
            {parseInt(creditsForm.delta, 10) < 0 && (
              <div className="space-y-2">
                <Label htmlFor="credits_dealer">Deducting for which dealer? *</Label>
                <Select value={creditsForm.dealer_id}
                        onValueChange={(v) => setCreditsForm(f => ({ ...f, dealer_id: v }))}>
                  <SelectTrigger id="credits_dealer" data-testid="credits-dealer-select">
                    <SelectValue placeholder="Select a dealer" />
                  </SelectTrigger>
                  <SelectContent>
                    {conquest.dealers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Shown to the reseller so they can confirm we only deducted what they requested.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="credits_note">Note (optional)</Label>
              <Input id="credits_note" placeholder="e.g. Wave checkout #1234"
                     value={creditsForm.note}
                     onChange={(e) => setCreditsForm(f => ({ ...f, note: e.target.value }))}
                     data-testid="credits-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCreditsModal(false)} disabled={savingCredits}>Cancel</Button>
            <Button onClick={handleAddCredits} disabled={savingCredits} data-testid="submit-add-credits">
              {savingCredits ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dealer Link Modal */}
      <Dialog open={showDealerLinkModal} onOpenChange={(open) => { if (!open) setShowDealerLinkModal(false); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingDealerLink ? "Edit" : "Add"} Conquest Dealer</DialogTitle>
            <DialogDescription>
              A lightweight dealer entry just for Conquest Audits — name plus a report link and a demo link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="conquest_dealer_name">Dealer Name *</Label>
              <Input id="conquest_dealer_name" placeholder="e.g., ABC Motors"
                     value={dealerLinkForm.name}
                     onChange={(e) => setDealerLinkForm(f => ({ ...f, name: e.target.value }))}
                     data-testid="conquest-dealer-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conquest_view_report_url">View Report URL</Label>
              <Input id="conquest_view_report_url" placeholder="https://..."
                     value={dealerLinkForm.view_report_url}
                     onChange={(e) => setDealerLinkForm(f => ({ ...f, view_report_url: e.target.value }))}
                     data-testid="conquest-dealer-report-url-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conquest_demo_url">Demo URL</Label>
              <Input id="conquest_demo_url" placeholder="https://..."
                     value={dealerLinkForm.demo_url}
                     onChange={(e) => setDealerLinkForm(f => ({ ...f, demo_url: e.target.value }))}
                     data-testid="conquest-dealer-demo-url-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDealerLinkModal(false)} disabled={savingDealerLink}>Cancel</Button>
            <Button onClick={handleSaveDealerLink} disabled={savingDealerLink} data-testid="submit-conquest-dealer">
              {savingDealerLink ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dealer Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) { setShowCreateModal(false); resetCreateForm(); } }}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Create Dealer for {reseller.name}</DialogTitle>
            <DialogDescription>
              Create a new dealer and automatically assign it to this reseller.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dealer_name">Dealer Name *</Label>
              <Input
                id="dealer_name"
                placeholder="e.g., ABC Motors"
                value={createForm.dealer_name}
                onChange={(e) => setCreateForm(f => ({ ...f, dealer_name: e.target.value }))}
                data-testid="create-dealer-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dealer_admin_email">Dealer Admin Email (optional)</Label>
              <Input
                id="dealer_admin_email"
                type="email"
                placeholder="admin@dealer.com"
                value={createForm.dealer_admin_email}
                onChange={(e) => setCreateForm(f => ({ ...f, dealer_admin_email: e.target.value }))}
                data-testid="create-dealer-email-input"
              />
              <p className="text-xs text-gray-500">Leave blank to create dealer without an admin user</p>
            </div>
            
            {createForm.dealer_admin_email && (
              <div className="space-y-3">
                <Label>Authentication Setup</Label>
                <RadioGroup 
                  value={createForm.invite_mode} 
                  onValueChange={(v) => setCreateForm(f => ({ ...f, invite_mode: v }))}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="invite" id="invite" />
                    <Label htmlFor="invite" className="font-normal cursor-pointer">
                      Send invite email
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="temp_password" id="temp_password" />
                    <Label htmlFor="temp_password" className="font-normal cursor-pointer">
                      Set temporary password
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="none" />
                    <Label htmlFor="none" className="font-normal cursor-pointer">
                      No auth setup (configure later)
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
            
            {createForm.invite_mode === "temp_password" && (
              <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                <div className="space-y-2">
                  <Label htmlFor="temp_password">Password</Label>
                  <Input
                    id="temp_password"
                    type="password"
                    placeholder="Min 8 characters"
                    value={createForm.temp_password}
                    onChange={(e) => setCreateForm(f => ({ ...f, temp_password: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    placeholder="Confirm password"
                    value={createForm.confirm_password}
                    onChange={(e) => setCreateForm(f => ({ ...f, confirm_password: e.target.value }))}
                  />
                </div>
              </div>
            )}
            
            {/* Pricing Section */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium">Reseller Pricing (optional)</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reseller_cost" className="text-xs text-gray-500">Monthly Cost ($)</Label>
                  <Input
                    id="reseller_cost"
                    type="number"
                    placeholder="0"
                    value={createForm.reseller_cost}
                    onChange={(e) => setCreateForm(f => ({ ...f, reseller_cost: e.target.value }))}
                    data-testid="create-dealer-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reseller_retail" className="text-xs text-gray-500">Monthly Retail ($)</Label>
                  <Input
                    id="reseller_retail"
                    type="number"
                    placeholder="0"
                    value={createForm.reseller_retail}
                    onChange={(e) => setCreateForm(f => ({ ...f, reseller_retail: e.target.value }))}
                    data-testid="create-dealer-retail-input"
                  />
                </div>
              </div>
              {createForm.reseller_cost && createForm.reseller_retail && (
                <p className="text-xs text-green-600">
                  Monthly margin: {formatCurrency(parseFloat(createForm.reseller_retail) - parseFloat(createForm.reseller_cost))}
                </p>
              )}
            </div>
            
            {/* Reseller (locked) */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Assigned to Reseller</p>
              <p className="font-medium text-gray-900">{reseller.name}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetCreateForm(); }} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateDealer} disabled={creating} data-testid="submit-create-dealer">
              {creating ? "Creating..." : "Create Dealer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Existing Dealer Modal */}
      <Dialog open={showAssignModal} onOpenChange={(open) => { if (!open) { setShowAssignModal(false); resetAssignForm(); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Dealer to {reseller.name}</DialogTitle>
            <DialogDescription>
              Select an existing dealer to assign to this reseller.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Search Dealers</Label>
              <Input
                placeholder="Search by name..."
                value={dealerSearch}
                onChange={(e) => {
                  setDealerSearch(e.target.value);
                  fetchAvailableDealers(e.target.value);
                }}
                data-testid="dealer-search-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Select Dealer *</Label>
              {loadingDealers ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select 
                  value={assignForm.dealer_business_id} 
                  onValueChange={(v) => setAssignForm(f => ({ ...f, dealer_business_id: v }))}
                >
                  <SelectTrigger data-testid="dealer-select">
                    <SelectValue placeholder="Choose a dealer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDealers.length === 0 ? (
                      <div className="p-2 text-sm text-gray-500">No available dealers found</div>
                    ) : (
                      availableDealers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Pricing Section */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-sm font-medium">Reseller Pricing (optional)</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="assign_cost" className="text-xs text-gray-500">Monthly Cost ($)</Label>
                  <Input
                    id="assign_cost"
                    type="number"
                    placeholder="0"
                    value={assignForm.reseller_cost}
                    onChange={(e) => setAssignForm(f => ({ ...f, reseller_cost: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assign_retail" className="text-xs text-gray-500">Monthly Retail ($)</Label>
                  <Input
                    id="assign_retail"
                    type="number"
                    placeholder="0"
                    value={assignForm.reseller_retail}
                    onChange={(e) => setAssignForm(f => ({ ...f, reseller_retail: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignModal(false); resetAssignForm(); }} disabled={assigning}>
              Cancel
            </Button>
            <Button onClick={handleAssignDealer} disabled={assigning} data-testid="submit-assign-dealer">
              {assigning ? "Assigning..." : "Assign Dealer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResellerDetailPage;
